// REST + WS client for live meeting transcript (SD28).
// The extension offscreen document and the web preview both call this.

import { api } from "../api/client";
import { progressSocket } from "../api/ws";
import type {
  GrackleTranscript,
  LiveBookmark,
  LiveSession,
  LiveWsMessage,
  TranscriptLine,
} from "./types";

export async function createLiveSession(
  orgId: string,
  body: {
    grackle_id?: string;
    meet_url?: string;
    title?: string;
    consent_acknowledged?: boolean;
    /** F8 / FR-RD15: the desktop app recording a Zoom/Teams client window. */
    capture_platform?: "zoom_desktop" | "teams_desktop";
  } = {},
): Promise<LiveSession> {
  return api<LiveSession>("POST", `/orgs/${orgId}/live-transcripts`, body);
}

export async function getLiveSession(sessionId: string): Promise<LiveSession> {
  return api<LiveSession>("GET", `/live-transcripts/${sessionId}`);
}

export async function postAudioChunk(
  sessionId: string,
  chunk: {
    seq: number;
    t_ms: number;
    sample_rate?: number;
    channels?: number;
    pcm_s16le_b64?: string;
    peak?: number;
    speaker_index?: number;
  },
): Promise<{ accepted: boolean; seq: number; status: string; degrade_reason?: string; line?: TranscriptLine }> {
  return api("POST", `/live-transcripts/${sessionId}/audio`, chunk);
}

/** F17 §4: the user confirmed "I've announced it"; the server stamps `consent_announced_at`. */
export async function announceConsent(sessionId: string): Promise<LiveSession> {
  return api<LiveSession>("POST", `/live-transcripts/${sessionId}/consent-announced`);
}

export async function markMoment(
  sessionId: string,
  atMs: number,
  label = "",
): Promise<LiveBookmark> {
  return api("POST", `/live-transcripts/${sessionId}/bookmarks`, { at_ms: atMs, label });
}

export async function setParticipants(
  sessionId: string,
  participants: { speaker_index: number; display_name: string; platform_id?: string; email?: string }[],
): Promise<void> {
  await api("POST", `/live-transcripts/${sessionId}/participants`, { participants });
}

export async function degradeLive(sessionId: string, reason: "cpu" | "bandwidth" | "paused"): Promise<LiveSession> {
  return api("POST", `/live-transcripts/${sessionId}/degrade`, { reason });
}

export async function resumeLive(sessionId: string): Promise<LiveSession> {
  return api("POST", `/live-transcripts/${sessionId}/resume`);
}

export async function finalizeLive(sessionId: string, grackleId?: string): Promise<GrackleTranscript> {
  return api("POST", `/live-transcripts/${sessionId}/finalize`, { grackle_id: grackleId });
}

export async function getGrackleTranscript(
  grackleId: string,
  share?: string | null,
  /** F3: a protected share link's unlock header (X-Grackle-Unlock), when one was minted. */
  headers?: Record<string, string>,
): Promise<GrackleTranscript> {
  const qs = share ? `?share=${encodeURIComponent(share)}` : "";
  return api("GET", `/grackles/${grackleId}/transcript${qs}`, undefined, headers);
}

export type LiveListener = (msg: LiveWsMessage) => void;

/** Subscribe to SD28 pushes on the shared `/api/v1/ws` connection. */
export function subscribeLiveTranscript(sessionId: string, listener: LiveListener): () => void {
  return progressSocket.subscribeLive(sessionId, listener);
}
